import { useCallback, useMemo } from 'react';

import { studentKeys, useAddStudent } from '@givebacks/api/student';
import { useFormyValidator } from '@givebacks/formy';
import { useQueryClient } from '@tanstack/react-query';
import { ButtonMantine } from 'ui';

import { AddStudentForm } from './addStudentForm.types';

type AddStudentSubmitProps = {
  onSuccess: () => void;
  setError: () => void;
};

const AddStudentSubmit = ({ onSuccess, setError }: AddStudentSubmitProps) => {
  const { mutate: addStudent, isPending: isAddingStudent } = useAddStudent();
  const queryClient = useQueryClient();

  const { useGetForm } = useFormyValidator<AddStudentForm>();
  const form = useGetForm('add-student-form');

  const handleSubmit = useCallback(() => {
    const isValid = form.validate().hasErrors === false;
    if (isValid) {
      const formValues = form.values;
      addStudent(
        {
          first_name: formValues.firstName,
          last_name: formValues.lastName,
          student_id: formValues.studentId,
        },
        {
          onSuccess: () => {
            /** Invalidate all consumer student queries to ensure students and fees are refetched*/
            void queryClient.invalidateQueries({
              queryKey: studentKeys.id('user').consumer.all,
            });
            onSuccess();
          },
          onError: () => {
            setError();
          },
        }
      );
    }
  }, [form, addStudent, queryClient, onSuccess, setError]);

  const isLoading = useMemo(() => {
    return isAddingStudent;
  }, [isAddingStudent]);

  return (
    <ButtonMantine w="100%" onClick={handleSubmit} loading={isLoading}>
      Add Student
    </ButtonMantine>
  );
};

export { AddStudentSubmit };
